/**
 * i18n setup — fr embarqué, 11 autres langues chargées à la demande.
 *
 * Au boot, seul fr.json est dans le bundle initial (~14 kB). Quand l'utilisateur
 * change de langue, le JSON cible est `fetch()` via Vite dynamic import — Vite
 * crée un chunk séparé par langue (préchargé après le first paint si on veut).
 *
 * Détection : localStorage > navigator > fallback fr.
 * RTL : <html dir> auto-sync pour l'arabe.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import fr from './locales/fr.json';

// Lot 1 (2026-09-03) — décision Laurent : trois langues seulement pour le
// lancement Mars (FR, EN, NL). Les neuf autres fichiers de locale restent en
// place et se réactivent en rajoutant une ligne ici — les nouvelles clés ne
// sont plus traduites que dans ces trois langues.
export const SUPPORTED_LANGS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
] as const;

export type LangCode = (typeof SUPPORTED_LANGS)[number]['code'];

/**
 * Dynamic loaders by code — Vite splits each into its own chunk.
 * Static map (instead of a templated import) is required so Vite can
 * statically discover and split the modules at build time.
 */
// Les 12 fichiers restent chargeables (clé = code ISO) ; seuls les codes de
// SUPPORTED_LANGS sont proposés et acceptés par i18next (supportedLngs).
const LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  fr: () => Promise.resolve({ default: fr as Record<string, unknown> }),
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
  de: () => import('./locales/de.json'),
  it: () => import('./locales/it.json'),
  pt: () => import('./locales/pt.json'),
  nl: () => import('./locales/nl.json'),
  pl: () => import('./locales/pl.json'),
  cs: () => import('./locales/cs.json'),
  ru: () => import('./locales/ru.json'),
  uk: () => import('./locales/uk.json'),
  ar: () => import('./locales/ar.json'),
};

const loadedLangs = new Set<LangCode>(['fr']);

/** Ensure the translations for the given language are loaded; idempotent. */
export async function ensureLangLoaded(code: LangCode): Promise<void> {
  if (loadedLangs.has(code)) return;
  const loader = LOADERS[code];
  if (!loader) return; // code hors catalogue : on reste sur le fallback FR
  const mod = await loader();
  i18n.addResourceBundle(code, 'translation', mod.default, true, true);
  loadedLangs.add(code);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { translation: fr },
    },
    fallbackLng: 'fr',
    supportedLngs: SUPPORTED_LANGS.map((l) => l.code),
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'nia-lang',
    },
  });

// If the detected language isn't FR, load it then re-announce the change so
// react-i18next refreshes the tree.
const detected = i18n.resolvedLanguage as LangCode | undefined;
if (detected && detected !== 'fr') {
  void ensureLangLoaded(detected).then(() => {
    void i18n.changeLanguage(detected);
  });
}

// Override changeLanguage so callers keep using i18n.changeLanguage(code)
// transparently — we just lazy-load the bundle first.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(i18n as any).changeLanguage = async (code: LangCode, ...rest: any[]) => {
  await ensureLangLoaded(code);
  return originalChangeLanguage(code, ...rest);
};

// Synchronize <html lang> + dir for RTL support
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
});

export default i18n;
